<template>
  <div class="app flex-row align-items-center">
    <div class="container">
      <b-row class="justify-content-center animated fadeIn">
        <b-col sm="12" md="10" lg="8" class="text-center">
          <img :src="logo" :alt="logoAlt" class="login-form-image mb-4" />

          <h1 class="mb-4">{{ t("Registration") }}</h1>

          <b-card no-body class="card-accent-primary p-3 p-sm-4 institution-number-card">
            <!-- Status message area - changes based on state -->
            <div class="status-message mb-4">
              <Transition name="fade" mode="out-in">
                <!-- Searching -->
                <p v-if="isValidating" key="validating" class="status-searching mb-0">
                  <b-spinner small class="me-2" />
                  <span>{{ t("Searching for institution...") }}</span>
                </p>

                <!-- Found -->
                <p v-else-if="institutionName" key="found" class="status-success mb-0">
                  <i class="fa fa-check-circle me-2" />
                  <span>{{ institutionName }}</span>
                </p>

                <!-- Not found -->
                <p v-else-if="hasError" key="error" class="status-error mb-0">
                  <i class="fa fa-times-circle me-2" />
                  <span>{{ t("Institution with this number was not found") }}</span>
                </p>

                <!-- Default -->
                <p v-else key="default" class="text-muted mb-0">
                  {{ t("Enter your institution number to continue") }}
                </p>
              </Transition>
            </div>

            <b-form @submit.prevent="onSubmit">
              <b-form-group class="mb-4">
                <label for="institution-number-input" class="form-label fw-bold">
                  {{ t("Institution number") }}
                </label>

                <b-form-input
                  id="institution-number-input"
                  v-model="slugInput"
                  :placeholder="t('e.g. ABC123')"
                  size="lg"
                  class="text-center"
                  :class="inputClass"
                  autofocus
                  @input="onInputChange"
                />
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                size="lg"
                class="w-100"
                :disabled="!isValid"
              >
                {{ t("Continue") }}
                <i class="fa fa-arrow-right ms-2" />
              </b-button>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRegisterModesQuery } from "../queries/useRegisterModesQuery";
import { watchDebounced } from "@vueuse/core";

const emit = defineEmits<{
  submit: [slug: string];
}>();

const { t } = useI18n();

const slugInput = ref("");
const slugToValidate = ref<string | undefined>(undefined);
const hasError = ref(false);
const isValidating = ref(false);

const logo = computed(() => "/img/obiado.svg");
const logoAlt = computed(() => "Obiado logo");

// Query for validating the slug
const {
  data: modesData,
  isPending,
  error,
} = useRegisterModesQuery(slugToValidate, {
  enabled: computed(() => !!slugToValidate.value),
});

// Watch for query loading state
watch(isPending, (pending) => {
  if (slugToValidate.value) {
    isValidating.value = pending;
  }
});

// Watch for query error
watch(error, (err) => {
  if (err) {
    hasError.value = true;
    isValidating.value = false;
  }
});

// Watch for successful response
watch(modesData, (data) => {
  if (data) {
    isValidating.value = false;
    // If institution was not found (no name returned), show error
    hasError.value = !data.institutionName;
  }
});

// Debounced validation trigger
watchDebounced(
  slugInput,
  (value) => {
    const trimmed = value.trim();
    if (trimmed && trimmed.length >= 2) {
      isValidating.value = true;
      slugToValidate.value = trimmed;
    } else {
      slugToValidate.value = undefined;
      hasError.value = false;
      isValidating.value = false;
    }
  },
  { debounce: 700 },
);

// compares against the live input, not the debounced value, so a stale response can't enable Continue
const institutionName = computed(() => {
  if (!modesData.value || modesData.value.institutionSlug !== slugInput.value.trim()) {
    return null;
  }
  return modesData.value.institutionName;
});

const isValid = computed(() => {
  return !isValidating.value && !hasError.value && !!institutionName.value;
});

const inputClass = computed(() => {
  if (hasError.value) return "input-error";
  if (institutionName.value) return "input-success";
  return "";
});

function onInputChange() {
  // Reset states when user starts typing
  hasError.value = false;
  isValidating.value = false;
}

function onSubmit() {
  if (!isValid.value) {
    return;
  }

  emit("submit", slugInput.value.trim());
}

function setError() {
  hasError.value = true;
  isValidating.value = false;
}

function clearError() {
  hasError.value = false;
  isValidating.value = false;
}

defineExpose({ setError, clearError });
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

#institution-number-input {
  border-radius: 15px !important;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &.input-success {
    border-color: #28a745;
    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.15);
  }

  &.input-error {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
  }
}

.institution-number-card {
  border: 1px solid #d7d7d7 !important;
}

.status-message {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.status-searching {
  color: #6c757d;
}

.status-success {
  color: #155724;
  font-weight: 500;
}

.status-error {
  color: #dc3545;
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
